import { AbstractControl, Validators } from '@angular/forms';
import { PhoneNumberUtil } from 'google-libphonenumber';

/**
 * Basic Angular form validator for phone number format.
 * Checks that the value contains only digits, spaces, dashes, dots, parentheses, and an optional leading '+'.
 *
 * Used in: legacy phone-only fields and as a format pre-check inside `phoneValidNumberValidator`.
 * Prefer `phoneValidNumberValidator` for new fields.
 *
 * @returns `{ invalidPhoneNumber }` if the format is invalid, otherwise null.
 */
export function phoneValidator(control: AbstractControl): null | { invalidPhoneNumber: true } {
    return Validators.pattern(String.raw`^[+]?[0-9 ()\-.]*$`)(control) === null ? null : { invalidPhoneNumber: true };
}

export const phoneNumberUtil = PhoneNumberUtil.getInstance();
const { ValidationResult } = PhoneNumberUtil;

export type PhoneValidationErrors =
    | { phoneTooShort: true }
    | { phoneTooLong: true }
    | { invalidPhoneCountryCode: true }
    | { invalidPhoneNumber: true };

// Error message strings from google-libphonenumber's internal Error object.
// The library has no TypeScript declaration for this export, so we mirror the values here.
// If a library upgrade changes these strings, the catch-block matching silently degrades.
// The spec tests for '+000435455' (invalidPhoneCountryCode) and '+3725' (phoneTooShort) cover
// both catch paths end-to-end and will fail if the strings change.
const PHONE_PARSE_ERRORS = {
    INVALID_COUNTRY_CODE: 'Invalid country calling code',
    TOO_SHORT_NSN: 'The string supplied is too short to be a phone number'
} as const;

/**
 * Angular form validator for phone number fields that require deep validation.
 * Uses google-libphonenumber to verify the number is structurally valid for its country.
 * Input must be in E.164 format (e.g. +37255512345).
 *
 * Used in: registration, BankID complete, account edit, account detail (customer-account-detail-stw).
 * Also consumed by `emailOrPhoneValidator` for email-or-phone fields.
 *
 * Returns specific errors to allow distinct user-facing messages:
 * - `{ phoneTooShort }` — TOO_SHORT
 * - `{ phoneTooLong }` — TOO_LONG
 * - `{ invalidPhoneCountryCode }` — INVALID_COUNTRY_CODE
 * - `{ invalidPhoneNumber }` — parse succeeded but invalid for other reasons (e.g. INVALID_LENGTH), or completely unparseable input
 *
 * @see PhoneValidationErrors
 * @see emailOrPhoneValidator
 */

export function phoneValidNumberValidator(control: AbstractControl): null | PhoneValidationErrors {
    const value = control.value;
    if (!value) return null;

    const formatError = phoneValidator(control);
    if (formatError !== null) return formatError;

    try {
        const phoneNumber = phoneNumberUtil.parseAndKeepRawInput(value);

        const reason = phoneNumberUtil.isPossibleNumberWithReason(phoneNumber);
        if (reason === ValidationResult.TOO_SHORT) return { phoneTooShort: true };
        if (reason === ValidationResult.TOO_LONG) return { phoneTooLong: true };

        if (phoneNumberUtil.isValidNumber(phoneNumber)) return null;
        return { invalidPhoneNumber: true };
    } catch (e) {
        // parseAndKeepRawInput throws for numbers that fail pre-parse checks.
        // Inspect the message to surface specific errors rather than always returning generic.
        if (e instanceof Error) {
            if (e.message === PHONE_PARSE_ERRORS.INVALID_COUNTRY_CODE && value.startsWith('+'))
                return { invalidPhoneCountryCode: true };
            if (e.message === PHONE_PARSE_ERRORS.TOO_SHORT_NSN) return { phoneTooShort: true };
        }
        return { invalidPhoneNumber: true };
    }
}
